"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HighlightedTerm = void 0;
var react_1 = __importStar(require("react"));
var EntityPopupHost_1 = require("../EntityPopup/EntityPopupHost");
var TermPopup_1 = require("./TermPopup");
require("./styles/highlighted-term.scss");
var AFTERGLOW_DURATION_MS = 700;
/**
 * Inline text indicator. On hover it shows a small tooltip hint, on
 * click it animates and opens a `TermPopup` anchored to itself. After
 * the popup closes a brief afterglow plays so the reader can see which
 * term they just inspected.
 */
exports.HighlightedTerm = function (_a) {
    var term = _a.term, _b = _a.variant, variant = _b === void 0 ? 'definition' : _b, popupKind = _a.popupKind, hoverHint = _a.hoverHint, resolveContent = _a.resolveContent, onTermClick = _a.onTermClick, elementId = _a.elementId, useCaseId = _a.useCaseId, className = _a.className, children = _a.children;
    var anchorRef = react_1.useRef(null);
    var afterglowTimerRef = react_1.useRef(null);
    var _c = react_1.useState(false), isOpen = _c[0], setIsOpen = _c[1];
    var _d = react_1.useState(false), isAfterglow = _d[0], setIsAfterglow = _d[1];
    var _e = react_1.useState(null), popupContent = _e[0], setPopupContent = _e[1];
    react_1.useEffect(function () {
        return function () {
            if (afterglowTimerRef.current !== null) {
                window.clearTimeout(afterglowTimerRef.current);
            }
        };
    }, []);
    var buildPayload = react_1.useCallback(function () {
        var node = anchorRef.current;
        var htmlProps = {
            'data-term': term,
            'data-variant': variant,
        };
        if (node) {
            // Capture every data-* attribute on the rendered element as a
            // mock for the future props/payload contract.
            var attrs = node.attributes;
            for (var i = 0; i < attrs.length; i += 1) {
                var a = attrs.item(i);
                if (a && a.name.indexOf('data-') === 0) {
                    htmlProps[a.name] = a.value;
                }
            }
        }
        var rect = node && node.getBoundingClientRect
            ? node.getBoundingClientRect()
            : { top: 0, left: 0, width: 0, height: 0 };
        return {
            term: term,
            variant: variant,
            htmlProps: htmlProps,
            rect: {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            },
        };
    }, [term, variant]);
    var handleClick = react_1.useCallback(function (event) {
        event.stopPropagation();
        // Blur the trigger before the modal mounts so Fluent's
        // FocusTrapZone records `body` as the previously-focused element
        // rather than this span. That avoids the on-close jump where the
        // browser scroll-into-views the trigger when it's offscreen.
        var node = event.currentTarget;
        if (node && typeof node.blur === 'function') {
            node.blur();
        }
        var payload = buildPayload();
        if (onTermClick) {
            onTermClick(payload);
        }
        // Resolve content via provider, fall back to a sensible mock so
        // the popup still has something to render during integration.
        var resolved = resolveContent ? resolveContent(payload) : undefined;
        setPopupContent(resolved || {
            title: term,
            kicker: variant.toUpperCase(),
            body: 'Description for this term will be provided shortly. ' +
                'For now this is a placeholder so the popup layout can be reviewed.',
            variant: variant,
        });
        setIsOpen(true);
    }, [buildPayload, onTermClick, resolveContent, term, variant]);
    var handleKeyDown = react_1.useCallback(function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick(event);
        }
    }, [handleClick]);
    var handleDismiss = react_1.useCallback(function () {
        setIsOpen(false);
        // After-close highlight pulse so the reader can spot which term
        // they were just reading about.
        setIsAfterglow(true);
        if (afterglowTimerRef.current !== null) {
            window.clearTimeout(afterglowTimerRef.current);
        }
        afterglowTimerRef.current = window.setTimeout(function () {
            setIsAfterglow(false);
            afterglowTimerRef.current = null;
        }, AFTERGLOW_DURATION_MS);
    }, []);
    var rootClasses = ['ws-highlighted-term', "ws-highlighted-term--" + variant];
    if (isOpen) {
        rootClasses.push('is-open');
    }
    if (isAfterglow) {
        rootClasses.push('is-afterglow');
    }
    if (className) {
        rootClasses.push(className);
    }
    var tooltipText = hoverHint || "Click to learn more about \"" + term + "\"";
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("span", { ref: anchorRef, className: rootClasses.join(' '), role: "button", tabIndex: 0, title: tooltipText, "aria-label": tooltipText, "aria-expanded": isOpen, "data-term": term, "data-variant": variant, onClick: handleClick, onKeyDown: handleKeyDown }, children || term),
        isOpen && popupKind ? (react_1.default.createElement(EntityPopupHost_1.EntityPopupHost, { kind: popupKind, term: term, isOpen: isOpen, onDismiss: handleDismiss, elementId: elementId, useCaseId: useCaseId })) : null,
        isOpen && !popupKind && popupContent ? (react_1.default.createElement(TermPopup_1.TermPopup, { target: anchorRef.current, content: popupContent, onDismiss: handleDismiss })) : null));
};
